// src/pdf/clientPdf.js
//
// Generazione PDF lato browser: nessun carico computazionale sul backend.
// I template vengono scaricati una volta sola e messi in Cache Storage,
// quindi funzionano anche offline.
//
// Mail, notifica e push NON sono piu un effetto collaterale della
// generazione: chi compila un verbale passa inviaReport: true e il PDF gia
// pronto viene spedito a /invia-report. Chi si limita a ristampare un
// verbale gia archiviato non notifica nessuno.
//
// Se la generazione locale fallisce si ricade sul vecchio endpoint
// genera-pdf, che genera e notifica come ha sempre fatto.

import { authFetch } from '../utils/authFetch.js';
import {
  buildManutenzionePdf,
  manutenzioneTemplateFileName,
  resolveManutenzioneLayout,
} from './manutenzionePdf.js';
import { buildVitPdf, vitTemplateFileName } from './vitPdf.js';

const TEMPLATE_CACHE = 'ciesse-pdf-templates-v1';
// Con CRA era process.env.PUBLIC_URL. In Vite il prefisso della cartella
// public e' BASE_URL, che vale '/' quando l'app sta alla radice del dominio:
// la barra finale va tolta perche' i percorsi qui sotto iniziano gia' con '/'.
const PUBLIC_URL = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');

// Cache in memoria per la sessione corrente
const memoryCache = new Map();

/**
 * Scarica i byte di un template PDF da /public, con cache persistente.
 * @param {string} url es. /Manutenzione/CiesseImpianti.pdf
 * @returns {Promise<ArrayBuffer>}
 */
async function loadTemplateBytes(url) {
  if (memoryCache.has(url)) return memoryCache.get(url);

  let response;

  // 1) Cache Storage (sopravvive al reload ed e disponibile offline)
  if (typeof caches !== 'undefined') {
    try {
      const cache = await caches.open(TEMPLATE_CACHE);
      const cached = await cache.match(url);

      if (cached) {
        response = cached;
      } else {
        const fresh = await fetch(url);
        if (!fresh.ok) throw new Error(`Template non raggiungibile (${fresh.status})`);
        await cache.put(url, fresh.clone());
        response = fresh;
      }
    } catch (err) {
      // Cache Storage non disponibile (es. contesto non sicuro): si prosegue
      console.warn('Cache template PDF non disponibile:', err);
    }
  }

  // 2) Fallback: fetch diretto
  if (!response) {
    response = await fetch(url);
    if (!response.ok) throw new Error(`Template non raggiungibile (${response.status})`);
  }

  const bytes = await response.arrayBuffer();
  memoryCache.set(url, bytes);
  return bytes;
}

/**
 * sha256 esadecimale del template, per riconoscere quale modulo e in uso.
 * Richiede un contesto sicuro (https o localhost): senza crypto.subtle si
 * restituisce null e resolveManutenzioneLayout usa il layout di ripiego.
 */
async function sha256Hex(bytes) {
  const subtle =
    typeof window !== 'undefined' && window.crypto ? window.crypto.subtle : null;
  if (!subtle) return null;

  const digest = await subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Blob -> base64, a blocchi per non saturare lo stack sui PDF grandi. */
async function blobToBase64(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
}

/**
 * Pre-scarica tutti i template PDF (da chiamare quando si e online,
 * es. all'apertura dell'app), cosi la generazione funziona anche offline.
 */
export async function prefetchPdfTemplates() {
  const urls = [
    `${PUBLIC_URL}/Manutenzione/CiesseImpianti.pdf`,
    `${PUBLIC_URL}/Manutenzione/CiesseService.pdf`,
    `${PUBLIC_URL}/VIT/CiesseImpianti.pdf`,
    `${PUBLIC_URL}/VIT/CiesseService.pdf`,
  ];

  const results = await Promise.allSettled(urls.map((u) => loadTemplateBytes(u)));
  return results.every((r) => r.status === 'fulfilled');
}

/** Chiamata al vecchio endpoint, che genera E notifica. Solo come fallback. */
async function generateOnServer(endpoint, body) {
  const res = await authFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res || !res.ok) {
    const data = res ? await res.json().catch(() => null) : null;
    throw new Error(data?.error || 'Errore nella generazione del PDF');
  }

  return res.blob();
}

/** Manda il PDF gia generato: solo mail + notifica + push. */
async function inviaReport(endpoint, blob, formData) {
  const res = await authFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ formData, pdfBase64: await blobToBase64(blob) }),
  });

  if (!res || !res.ok) {
    const data = res ? await res.json().catch(() => null) : null;
    throw new Error(data?.error || 'Errore invio del report');
  }

  return res.json().catch(() => null);
}

/**
 * Verbale di MANUTENZIONE.
 *
 * @param {Object} params
 * @param {boolean=} params.inviaReport true quando il verbale viene compilato
 *   (manda mail + notifica + push). false per una semplice ristampa.
 * @returns {Promise<Blob>}
 */
export async function generateManutenzionePdfBlob({
  template,
  formData = {},
  checks = {},
  tecnicoSignature = null,
  clienteSignature = null,
  inviaReport: notifica = false,
}) {
  let blob;

  try {
    const url = `${PUBLIC_URL}/Manutenzione/${manutenzioneTemplateFileName(template)}`;
    const templateBytes = await loadTemplateBytes(url);
    const layout = resolveManutenzioneLayout(await sha256Hex(templateBytes), template);

    const pdfBytes = await buildManutenzionePdf({
      templateBytes,
      layout,
      formData,
      checks,
      tecnicoSignature,
      clienteSignature,
    });

    blob = new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (err) {
    console.warn('Generazione PDF manutenzione locale fallita, uso il server:', err);

    // Questo endpoint notifica gia per conto suo: non si prosegue oltre.
    return generateOnServer('/api/manutenzione/genera-pdf', {
      template,
      formData,
      checks,
      tecnicoSignature,
      clienteSignature,
    });
  }

  if (notifica) {
    await inviaReport('/api/manutenzione/invia-report', blob, formData);
  }

  return blob;
}

/**
 * Verbale di Intervento Tecnico (VIT).
 *
 * @param {Object} params
 * @param {boolean=} params.inviaReport true quando il verbale viene compilato.
 * @returns {Promise<Blob>}
 */
export async function generateVitPdfBlob({
  template,
  formData = {},
  tecnicoSignature = null,
  clienteSignature = null,
  inviaReport: notifica = false,
}) {
  let blob;

  try {
    const url = `${PUBLIC_URL}/VIT/${vitTemplateFileName(template)}`;
    const templateBytes = await loadTemplateBytes(url);

    const pdfBytes = await buildVitPdf({
      templateBytes,
      formData,
      tecnicoSignature,
      clienteSignature,
    });

    blob = new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (err) {
    console.warn('Generazione PDF VIT locale fallita, uso il server:', err);

    return generateOnServer('/api/vit/genera-pdf', {
      template,
      formData,
      tecnicoSignature,
      clienteSignature,
    });
  }

  if (notifica) {
    await inviaReport('/api/vit/invia-report', blob, formData);
  }

  return blob;
}

/** Scarica un Blob come file. */
export function downloadPdfBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'documento.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
